/**
 * Thin fetch wrapper for the EdgeFinances backend API.
 * Base URL comes from REACT_APP_API_URL (default http://localhost:5000/api).
 * The JWT returned by /auth is stored in localStorage under `authToken`.
 */

import { getActiveLedgerId, isViewAllLedgers } from '../lib/ledger';

const AUTH_TOKEN_KEY = 'authToken';
const CUSTOMER_TOKEN_KEY = 'customerToken';

/**
 * Resolve the API base URL from REACT_APP_API_URL (baked at build time).
 * - Strips any trailing slash so paths like `/auth/login` don't produce `//`.
 * - Appends `/api` if the configured URL points at the host root, so setting the
 *   var to `https://host` or `https://host/` still works.
 */
function resolveApiBaseUrl(): string {
  // CRA/webpack inlines process.env.REACT_APP_API_URL at build time — do not guard
  // with `typeof process` or the browser falls back to localhost at runtime.
  const raw = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  let base = raw.trim().replace(/\/+$/, '');
  if (!/\/api(\/|$)/.test(base)) base = `${base}/api`;
  return base;
}

export const SESSION_EXPIRED_EVENT = 'ef-session-expired';
export const API_BASE_URL = resolveApiBaseUrl();

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearAuthToken(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** True once an admin/super-admin JWT is present (i.e. backend-backed session). */
export function hasAuthToken(): boolean {
  return !!getAuthToken();
}

export function getCustomerToken(): string | null {
  try {
    return localStorage.getItem(CUSTOMER_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setCustomerToken(token: string): void {
  try {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearCustomerToken(): void {
  try {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function hasCustomerToken(): boolean {
  return !!getCustomerToken();
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
  /** When set, use this token (and don't fall back to the admin token). */
  token?: string | null;
};

export async function apiRequest<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal, token } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (auth) {
    const adminToken = getAuthToken();
    if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  }

  if (auth && !token) {
    if (isViewAllLedgers()) {
      headers['X-View-All-Ledgers'] = 'true';
    } else {
      const ledgerId = getActiveLedgerId();
      if (ledgerId) headers['X-Ledger-Id'] = ledgerId;
    }
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    // Network/CORS failure — surface as a 0-status ApiError so callers can fall back.
    throw new ApiError(
      'Cannot reach server. Check internet — some Wi‑Fi networks block cloud APIs; try mobile data/hotspot, or wait and retry.',
      0
    );
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const message =
      (payload && typeof payload === 'object' && 'error' in payload && String((payload as { error: unknown }).error)) ||
      `Request failed (${res.status})`;
    if (res.status === 401 && auth && !token) {
      clearAuthToken();
      clearCustomerToken();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    }
    throw new ApiError(message, res.status);
  }

  return payload as T;
}
